import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import styles from './EftDetailsForm.styles';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IActionItem } from 'components/common/Modals/FormViewModal.properties';
import IEftDetailsFormProps from './EftDetailsForm.properties';
import NumericField from '@markinson/uicomponents-v2/NumericField';
import TextField from '@markinson/uicomponents-v2/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { ProcessValidationFormsContext } from 'utils/processValidationForms';
import { IUpdatePaymentLineDetailFacade } from 'api/swaggerTypes';
import { ObjectifiedPaymentLineDetailsResponse } from 'api/Worksale/payment';
import { TITLE, APPLYLABEL, CANCELLABEL } from './EftDetailsForm.constants';
import ITextFieldHandle from '@markinson/uicomponents-v2/TextField/TextField.handle';

const EftDetailsForm = (props: IEftDetailsFormProps): JSX.Element => {
  const { classes, open, initialValues, schema, paymentDetail, isProcessing, onCalculatePaymentLineDetails, onApply, onCancel } = props;

  const processValidationForms = React.useContext(ProcessValidationFormsContext);

  const [values, setValues] = React.useState(initialValues);
  const [amount, setAmount] = React.useState<number>(0);
  const [cashOut, setCashOut] = React.useState<number>(0);
  const [waiveMerchantFeeSurcharge, setWaiveMerchantFeeSurcharge] = React.useState<boolean>(true);
  const [merchantFeeRecovery, setMerchantFeeRecovery] = React.useState<number>(0);
  const [totalAmount, setTotalAmount] = React.useState<number>(0);
  const [reference, setReference] = React.useState<string>('');
  const [calculateInProgress, setCalculateInProgress] = React.useState<boolean>(false);
  const [apply, setApply] = React.useState(false);

  const referenceFieldRef = React.useRef<ITextFieldHandle>();
  const paymentAmountFieldRef = React.useRef<ITextFieldHandle>();

  React.useEffect(
    () => {
      setValues(initialValues);
      if (initialValues) {
        setAmount(values.Amount ? values.Amount : 0);
        setCashOut(values.CashOut ? values.CashOut : 0);
        setWaiveMerchantFeeSurcharge(values.WaiveMerchantFeeSurcharge || false);
        setReference(values.Reference);
      } else {
        setAmount(0);
        setCashOut(0);
        setWaiveMerchantFeeSurcharge(false);
        setReference('');
      }
    },
    [initialValues]);

  React.useEffect(
    () => {
      if (values) {
        setMerchantFeeRecovery(values.MerchantFeeRecovery ? values.MerchantFeeRecovery : 0);
        setTotalAmount(values.TotalAmount ? values.TotalAmount : 0);
      } else {
        setMerchantFeeRecovery(0);
        setTotalAmount(0);
      }
    },
    [values]);

  React.useEffect(
    () => {
      if (!calculateInProgress && apply) {
        handleOnApply();
      }
    },
    [apply, calculateInProgress]);

  async function onCalculatePaymentLineDetailsAndValidate(query: IUpdatePaymentLineDetailFacade, ChangedField: string): Promise<ObjectifiedPaymentLineDetailsResponse> {
    return processValidationForms(await onCalculatePaymentLineDetails(query, ChangedField), query, async (newQuery) => onCalculatePaymentLineDetailsAndValidate(newQuery, ChangedField));
  }

  async function handleValuesChanged(newValues: IUpdatePaymentLineDetailFacade['UpdateLine'], ChangedField: string): Promise<void> {
    if (onCalculatePaymentLineDetails) {
      try {
        setCalculateInProgress(true);
        const response = await onCalculatePaymentLineDetailsAndValidate(
          {
            PaymentDetail: paymentDetail,
            UpdateLine: newValues
          },
          ChangedField);

        if (response.Status && response.PaymentLineDetail) {
          setValues(response.PaymentLineDetail.inlineObject);
        }
      } finally {
        setCalculateInProgress(false);
      }
    }
  }

  const handleOnApply = async () => {
    if (!hasErrors()) {
      if (onApply) {
        onApply({
          ...values,
          Amount: amount,
          CashOut: cashOut,
          WaiveMerchantFeeSurcharge: waiveMerchantFeeSurcharge,
          MerchantFeeRecovery: merchantFeeRecovery,
          TotalAmount: totalAmount,
          Reference: reference
        });
      }
    } else {
      referenceFieldRef.current?.validate();
    }
  };

  const isOkButtonPressed = (event) => {
    if (event?.relatedTarget?.innerText === APPLYLABEL) {
      setApply(true);
    }
  };

  const hasErrors = () => {
    if (calculateInProgress ||
      (schema.Amount.Required && amount === undefined) || (amount !== undefined && amount <= 0) ||
      (schema.CashOut.Required && !cashOut) || (cashOut !== undefined && cashOut < 0) ||
      (schema.Reference.Required && !reference)) {

      return true;
    }

    return false;
  };

  const focusField = () => {
    setTimeout(
      () => {
        paymentAmountFieldRef?.current?.focus();
      },
      0
    );
  };

  React.useEffect(
    () => {
      const handleKeyDown = (event) => {
        const isCancelButton = event.target instanceof HTMLButtonElement && event.target.innerText === 'Cancel';

        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();

          if (isCancelButton && onCancel) {
            onCancel();

            return;
          }

          if (!hasErrors()) {
            handleOnApply();
          } else {
            referenceFieldRef.current?.validate();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    },
    [hasErrors, handleOnApply]
  );

  const getModalContent = () => {
    return <div className={classes.mainDiv}>
      <div className={classes.leftColumnDiv}>
        <NumericField
          ref={paymentAmountFieldRef}
          value={amount}
          label={'Payment Amount'}
          minValueExclusive={0}
          onBlur={(value, event) => {
            setAmount(value);
            handleValuesChanged(
              {
                ...values,
                Amount: value,
                CashOut: cashOut,
                WaiveMerchantFeeSurcharge: waiveMerchantFeeSurcharge,
                MerchantFeeRecovery: merchantFeeRecovery,
                TotalAmount: totalAmount,
                Reference: reference
              },
              'Amount').catch((err) => { console.warn(err); });
            isOkButtonPressed(event);
          }}
          required={schema.Amount.Required} />
        <NumericField
          value={cashOut}
          label={'Cash Out'}
          minValueInclusive={0}
          onBlur={(value, event) => {
            setCashOut(value);
            handleValuesChanged(
              {
                ...values,
                Amount: amount,
                CashOut: value,
                WaiveMerchantFeeSurcharge: waiveMerchantFeeSurcharge,
                MerchantFeeRecovery: merchantFeeRecovery,
                TotalAmount: totalAmount,
                Reference: reference
              },
              'CashOut').catch((err) => { console.warn(err); });
            isOkButtonPressed(event);
          }}
          required={schema.CashOut.Required} />
        <TextField
          innerRef={referenceFieldRef}
          value={reference}
          label={'Reference'}
          onChange={(value) => { setReference(value); }}
          required={schema.Reference.Required} />
      </div>
      <div className={classes.rightColumnDiv}>
        <FormControlLabel
          label={'Waive surcharge'}
          control={<Switch
            color={'default'}
            checked={waiveMerchantFeeSurcharge}
            onChange={(_, value) => {
              setWaiveMerchantFeeSurcharge(value);
              handleValuesChanged(
                {
                  ...values,
                  Amount: amount,
                  CashOut: cashOut,
                  WaiveMerchantFeeSurcharge: value,
                  MerchantFeeRecovery: merchantFeeRecovery,
                  TotalAmount: totalAmount,
                  Reference: reference
                },
                'WaiveMerchantFeeSurcharge').catch((err) => { console.warn(err); });
            }}
            classes={{
              switchBase: classes.switchBase,
              checked: classes.checked,
              bar: classes.bar,
            }}
          />}
          style={{ width: '100%' }}
          classes={{
            label: classes.formControlLabel
          }}
        />
        <NumericField
          value={merchantFeeRecovery}
          label={'Merchant fee recovery'}
          onChange={(value) => { setMerchantFeeRecovery(value); }}
          readonly />
        <NumericField
          value={totalAmount}
          label={'Total Payment'}
          onChange={(value) => { setTotalAmount(value); }}
          readonly />
      </div>
    </div >;
  };

  const confirmationActions = (): IActionItem[] => {
    return [
      {
        title: APPLYLABEL,
        isDefault: true,
        disabled: isProcessing,
        listener: handleOnApply
      },
      {
        title: CANCELLABEL,
        listener: async () => {
          if (onCancel) {
            onCancel();
          }
        }
      }
    ];
  };

  return (<FormViewModal
    open={open}
    loading={false}
    title={TITLE}
    modalContent={getModalContent()}
    actions={confirmationActions()}
    dialogActionsButtons={true}
    dialogActionsShadow={false}
    onActionShiftTabOut={focusField}
    onActionsTabOut={focusField}
  />);
};

export default withStyles(styles, { index: 1 })(EftDetailsForm);
